/**
 * Series labelling and colour — contract series → chart props.
 *
 * Colour rules are the prototype's (`data/aggregate.ts`): sentiment is pinned
 * (positive/neutral/negative = chart-4/5/6); any other dimension value takes
 * the palette slot of its position in the server's option order, wrapping at
 * the palette size; a single metric series is chart-1.
 */
import {
  chartPaletteSize,
  chartSeriesTokens,
  type ChartRow,
  type ChartSeries,
  type ChartSeriesToken,
} from '@deepdive-ui';

import type { DimensionKey, FilterOptions } from '@/data-sources/contract/filters';
import type { SeriesKey, TimeSeries } from '@/data-sources/contract/primitives';
import { readableLabel } from '@/lib/labels';

import { bucketLabel } from './buckets';
import { isoToMs } from './dates';
import { metricMeta, type MetricId } from './metrics';

/** The x key every chart row uses. Asserted collision-free by the mock engine. */
export const X_KEY = '__x';

export const SENTIMENT_TOKENS: Readonly<Record<string, ChartSeriesToken>> = {
  positive: 'chart-4',
  neutral: 'chart-5',
  negative: 'chart-6',
};

const SENTIMENT_ORDER = ['positive', 'neutral', 'negative'] as const;
export type Sentiment = (typeof SENTIMENT_ORDER)[number];

export function optionIndex(
  options: FilterOptions,
  dimension: DimensionKey,
  value: string,
): number {
  const index = options[dimension].findIndex((option) => option.value === value);
  return index < 0 ? 0 : index;
}

/** An option's label, readable (`digital_banking` → `Digital Banking`); an unknown key reads as itself. */
export function labelOf(options: FilterOptions, dimension: DimensionKey, value: string): string {
  return readableLabel(options[dimension].find((option) => option.value === value)?.label ?? value);
}

export function seriesToken(
  options: FilterOptions,
  dimension: DimensionKey,
  value: string,
): ChartSeriesToken {
  if (dimension === 'sentiment') {
    const byMeaning = SENTIMENT_TOKENS[value];
    if (byMeaning) return byMeaning;
  }
  return chartSeriesTokens[optionIndex(options, dimension, value) % chartPaletteSize]!;
}

/** Contract series → chart series (label + colour). */
export function chartSeriesFor(options: FilterOptions, keys: readonly SeriesKey[]): ChartSeries[] {
  return keys.map((s) =>
    s.dimension
      ? {
          key: s.key,
          label: labelOf(options, s.dimension, s.key),
          color: seriesToken(options, s.dimension, s.key),
        }
      : {
          key: s.key,
          label: metricMeta[s.key as MetricId]?.label ?? s.key,
          color: chartSeriesTokens[0]!,
        },
  );
}

export type TimeChart = { rows: ChartRow[]; xKey: typeof X_KEY; series: ChartSeries[] };

/** A contract time series as Recharts rows: one row per bucket, one key per series. */
export function timeChart(options: FilterOptions, ts: TimeSeries): TimeChart {
  const series = chartSeriesFor(options, ts.series);
  const rows: ChartRow[] = ts.x.map((iso, i) => {
    const row: ChartRow = { [X_KEY]: bucketLabel(isoToMs(iso), ts.bucket) };
    for (const s of ts.series) row[s.key] = s.values[i] ?? 0;
    return row;
  });
  return { rows, xKey: X_KEY, series };
}

export function dominantSentiment(
  counts: Readonly<Record<string, number>>,
): { sentiment: Sentiment; token: ChartSeriesToken; count: number; share: number } | null {
  let best: Sentiment | null = null;
  let bestCount = 0;
  let total = 0;
  for (const sentiment of SENTIMENT_ORDER) {
    const count = counts[sentiment] ?? 0;
    total += count;
    if (count > bestCount) {
      best = sentiment;
      bestCount = count;
    }
  }
  if (best === null || total === 0) return null;
  return {
    sentiment: best,
    token: SENTIMENT_TOKENS[best]!,
    count: bestCount,
    share: (bestCount / total) * 100,
  };
}
